import { useState, useEffect } from "react";
import { Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/enhanced-button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useOrganization } from "@/contexts/OrganizationContext";
import { createView, getView, updateView } from "@/services/view.service";

interface ViewModalProps {
  open: boolean;
  onClose: () => void;
  /** When set, edits this existing view instead of creating a new one. */
  viewId?: string;
}

export function ViewModal({ open, onClose, viewId }: ViewModalProps) {
  const { organizations, addView, setActiveView, updateViewName } = useOrganization();
  const isEditing = Boolean(viewId);
  const [name, setName] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !viewId) return;
    setIsLoading(true);
    setSubmitError(null);
    getView(viewId)
      .then((detail) => {
        setName(detail.name);
        setSelectedIds(new Set(detail.organizations.map((org) => org.id)));
      })
      .catch(() => setSubmitError("Failed to load view."))
      .finally(() => setIsLoading(false));
  }, [open, viewId]);

  const toggleOrg = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => {
    setName("");
    setSelectedIds(new Set());
    setSubmitError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim() || selectedIds.size === 0) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (viewId) {
        const updated = await updateView(viewId, { name: name.trim(), organizationIds: Array.from(selectedIds) });
        updateViewName(updated.id, updated.name);
      } else {
        const view = await createView({ name: name.trim(), organizationIds: Array.from(selectedIds) });
        addView({ id: view.id, name: view.name });
        setActiveView(view.id);
      }
      reset();
      onClose();
    } catch {
      setSubmitError(isEditing ? "Failed to update view. Please try again." : "Failed to create view. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            {isEditing ? "Edit view" : "Create a view"}
          </DialogTitle>
          <DialogDescription>
            A consolidated dashboard across the companies you choose.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-sm font-medium mb-2 block">View name</Label>
            <Input
              placeholder="e.g. All Restaurants"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">Companies</Label>
            {organizations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No companies found.</p>
            ) : (
              <ScrollArea className="max-h-40 border rounded-md p-2">
                <div className="space-y-2">
                  {organizations.map((org) => (
                    <div key={org.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`view-org-${org.id}`}
                        checked={selectedIds.has(org.id)}
                        onCheckedChange={() => toggleOrg(org.id)}
                        disabled={isLoading}
                      />
                      <label
                        htmlFor={`view-org-${org.id}`}
                        className="text-sm cursor-pointer select-none"
                      >
                        {org.name}
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!name.trim() || selectedIds.size === 0 || isSubmitting || isLoading}
            >
              {isSubmitting ? (isEditing ? "Saving…" : "Creating…") : isEditing ? "Save changes" : "Create view"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
